import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { PageContent } from "../components/PageContent";
import { Card } from "../components/Card";
import { Button } from "../components/Button";
import { LoadingContainer } from "../components/LoadingContainer";
import { InfoBox } from "../components/InfoBox";
import { SidebarPanel } from "../components/SidebarPanel";
import { AvailableBillingItemForm } from "./components/AvailableBillingItemForm";
import {
  Table,
  TableHeader,
  TableHeaderCell,
  TableBody,
  TableRow,
  TableCell,
} from "../components/Table";
import {
  useGetAvailableBillingItemsQuery,
  useCreateAvailableBillingItemMutation,
  useUpdateAvailableBillingItemMutation,
  useArchiveAvailableBillingItemMutation,
} from "../query/availableBillingItemsQuery";
import colors from "@styling/colors";
import { spacing } from "@styling/sizing";
import { fontSizes } from "@styling/fonts";
import type {
  RestAvailableBillingItem,
} from "@KassaroAB/shared/types/availableBillingItem";
import type { MultiCurrencyMoney } from "@KassaroAB/shared/types/multiCurrencyMoney";
import type { AvailableBillingItemId } from "@KassaroAB/shared/types/ids";
import { formatMoney } from "../utils/formatMoney";

const ArchiveButton = styled(Button)`
  background-color: ${colors.danger};
  padding: ${spacing.x1}px ${spacing.x2}px;
  font-size: ${fontSizes.small}px;
`;

const formatPrices = (price?: MultiCurrencyMoney): string => {
  if (!price || !price.currencies) return "-";
  return Object.values(price.currencies)
    .filter(Boolean)
    .map((money) => formatMoney(money!))
    .join(", ") || "-";
};

const formatPricing = (
  pricing: RestAvailableBillingItem["pricing"],
  t: (key: string) => string
): string => {
  const prices: string[] = [];
  if (pricing.monthlyPrice) {
    const formatted = formatPrices(pricing.monthlyPrice);
    if (formatted !== "-") prices.push(`${t("availableBillingItemsView.pricing.monthly")}: ${formatted}`);
  }
  if (pricing.oneTimePrice) {
    const formatted = formatPrices(pricing.oneTimePrice);
    if (formatted !== "-") prices.push(`${t("availableBillingItemsView.pricing.oneTime")}: ${formatted}`);
  }
  if (pricing.prepaid36MonthPrice) {
    const formatted = formatPrices(pricing.prepaid36MonthPrice);
    if (formatted !== "-") prices.push(`${t("availableBillingItemsView.pricing.prepaid36")}: ${formatted}`);
  }
  return prices.length > 0 ? prices.join(" | ") : "-";
};

export const AvailableBillingItemsView = () => {
  const { t } = useTranslation();
  const [selectedAvailableBillingItemId, setSelectedAvailableBillingItemId] = useState<AvailableBillingItemId | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [isPanelOpen, setIsPanelOpen] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const {
    data: availableBillingItems,
    isLoading: isLoadingAvailableBillingItems,
    error: availableBillingItemsError,
  } = useGetAvailableBillingItemsQuery();

  const { mutate: createAvailableBillingItem, error: createError } =
    useCreateAvailableBillingItemMutation();
  const { mutate: updateAvailableBillingItem, error: updateError } =
    useUpdateAvailableBillingItemMutation();
  const { mutate: archiveAvailableBillingItem, error: archiveError } =
    useArchiveAvailableBillingItemMutation();

  const visibleAvailableBillingItems = (availableBillingItems ?? []).filter(
    (item) => item.archivedAt == null,
  );

  const selectedAvailableBillingItem = visibleAvailableBillingItems.find(
    (item) => item.id === selectedAvailableBillingItemId,
  );

  const handleAvailableBillingItemClick = (item: RestAvailableBillingItem) => {
    setSelectedAvailableBillingItemId(item.id);
    setIsCreating(false);
    setIsPanelOpen(true);
  };

  const handleCreateClick = () => {
    setSelectedAvailableBillingItemId(null);
    setIsCreating(true);
    setIsPanelOpen(true);
  };

  const handleCreateAvailableBillingItem = (formData: FormData) => {
    createAvailableBillingItem(formData, {
      onSuccess: () => {
        setSuccessMessage(t("availableBillingItemsView.successCreated"));
        setIsPanelOpen(false);
        setIsCreating(false);
        setSelectedAvailableBillingItemId(null);
      },
      onError: () => {
        setSuccessMessage(null);
      },
    });
  };

  const handleUpdateAvailableBillingItem = (formData: FormData) => {
    if (!selectedAvailableBillingItemId) return;
    updateAvailableBillingItem(
      {
        availableBillingItemId: selectedAvailableBillingItemId,
        formData,
      },
      {
        onSuccess: () => {
          setSuccessMessage(t("availableBillingItemsView.successUpdated"));
          setIsPanelOpen(false);
          setSelectedAvailableBillingItemId(null);
        },
        onError: () => {
          setSuccessMessage(null);
        },
      },
    );
  };

  const handleArchiveAvailableBillingItem = (
    availableBillingItemId: AvailableBillingItemId,
    e: React.MouseEvent,
  ) => {
    e.stopPropagation();
    if (!window.confirm(t("availableBillingItemsView.archiveConfirm"))) {
      return;
    }
    archiveAvailableBillingItem(availableBillingItemId, {
      onSuccess: () => {
        setSuccessMessage(t("availableBillingItemsView.successArchived"));
      },
      onError: () => {
        setSuccessMessage(null);
      },
    });
  };

  const handlePanelClose = () => {
    setIsPanelOpen(false);
    setIsCreating(false);
    setSelectedAvailableBillingItemId(null);
  };

  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => {
        setSuccessMessage(null);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  if (isLoadingAvailableBillingItems) {
    return (
      <PageContent title={t("availableBillingItemsView.title")}>
        <LoadingContainer message={t("availableBillingItemsView.loading")} />
      </PageContent>
    );
  }

  return (
    <PageContent
      title={t("availableBillingItemsView.title")}
      actions={
        <Button onClick={handleCreateClick}>{t("availableBillingItemsView.createButton")}</Button>
      }
    >
      {availableBillingItemsError && (
        <InfoBox variant="error">
          {t("availableBillingItemsView.errorLoading")}
        </InfoBox>
      )}

      {(createError || updateError || archiveError) && (
        <InfoBox variant="error">
          {t("availableBillingItemsView.errorUpdating")}
        </InfoBox>
      )}

      {successMessage && (
        <InfoBox variant="success">{successMessage}</InfoBox>
      )}

      {visibleAvailableBillingItems.length > 0 ? (
        <Card>
          <Table>
            <TableHeader>
              <tr>
                <TableHeaderCell>{t("availableBillingItemsView.table.name")}</TableHeaderCell>
                <TableHeaderCell>{t("availableBillingItemsView.table.type")}</TableHeaderCell>
                <TableHeaderCell>{t("availableBillingItemsView.table.pricing")}</TableHeaderCell>
                <TableHeaderCell>{t("availableBillingItemsView.table.actions")}</TableHeaderCell>
              </tr>
            </TableHeader>
            <TableBody>
              {visibleAvailableBillingItems.map((item) => (
                <TableRow
                  key={item.id}
                  clickable
                  onClick={() => handleAvailableBillingItemClick(item)}
                >
                  <TableCell noWrap>{item.name}</TableCell>
                  <TableCell noWrap>{t(`billingItems.type.${item.type}`)}</TableCell>
                  <TableCell noWrap>{formatPricing(item.pricing, t)}</TableCell>
                  <TableCell noWrap>
                    <ArchiveButton onClick={(e) => handleArchiveAvailableBillingItem(item.id, e)}>
                      {t("availableBillingItemsView.archive")}
                    </ArchiveButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      ) : (
        <Card>
          <div style={{ textAlign: "center", color: colors.textMuted }}>
            {t("availableBillingItemsView.empty")}
          </div>
        </Card>
      )}

      {(selectedAvailableBillingItem || isCreating) && (
        <SidebarPanel
          isOpen={isPanelOpen}
          onClose={handlePanelClose}
          wide
          title={isCreating ? t("availableBillingItemsView.panelCreateTitle") : t("availableBillingItemsView.panelEditTitle", { name: selectedAvailableBillingItem!.name })}
        >
          <AvailableBillingItemForm
            availableBillingItem={selectedAvailableBillingItem || undefined}
            onSubmit={selectedAvailableBillingItem ? handleUpdateAvailableBillingItem : handleCreateAvailableBillingItem}
            onCancel={handlePanelClose}
          />
        </SidebarPanel>
      )}
    </PageContent>
  );
};
